import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, isApiError } from '../services/apiClient';
import type {
  RunningHubActiveJobSummary,
  RunningHubActiveJobsResponse,
  RunningHubFeatureType,
} from '../types/runningHubJobs';
import { RUNNING_HUB_FEATURE_LABELS } from '../types/runningHubJobs';
import type { RunningHubRecoveryRequest } from '../utils/applyRunningHubJobRecovery';
import { openPanelForRunningHubFeature } from '../utils/applyRunningHubJobRecovery';
import { evaluateRunningHubJobAutoAck } from '../utils/runningHubAutoAck';

const POLL_MS = 8000;

type Props = {
  language: 'zh' | 'en';
  isAuthenticated: boolean;
  userEmail?: string | null;
  onRecoverCompleted: (req: RunningHubRecoveryRequest) => void;
  panelOpeners: {
    setIsUpscaleOpen: (v: boolean) => void;
    setIsToneMappingOpen: (v: boolean) => void;
    setIsLightReconstructOpen: (v: boolean) => void;
    setIsTextReconstructOpen: (v: boolean) => void;
    setIsPhysicalReconstructOpen: (v: boolean) => void;
  };
};

function labelFor(feature: RunningHubFeatureType, language: 'zh' | 'en') {
  const row = RUNNING_HUB_FEATURE_LABELS[feature];
  return language === 'zh' ? row.zh : row.en;
}

export default function RunningHubJobRecoveryBar({
  language,
  isAuthenticated,
  userEmail,
  onRecoverCompleted,
  panelOpeners,
}: Props) {
  const zh = language === 'zh';
  const [processing, setProcessing] = useState<RunningHubActiveJobSummary[]>([]);
  const [completedUnacked, setCompletedUnacked] = useState<RunningHubActiveJobSummary[]>([]);
  const [globalMaintenance, setGlobalMaintenance] = useState(false);
  const [listDegraded, setListDegraded] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const appliedAckRef = useRef<Set<string>>(new Set());
  const autoAckInFlightRef = useRef<Set<string>>(new Set());

  const refreshActive = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = (await api.listActiveRunningHubJobs()) as RunningHubActiveJobsResponse;
      setGlobalMaintenance(false);
      setProcessing(Array.isArray(res?.processing) ? res.processing : []);
      setCompletedUnacked(Array.isArray(res?.completed_unacked) ? res.completed_unacked : []);
      setListDegraded(Boolean(res?.degraded) || (Array.isArray(res?.warnings) && res.warnings.length > 0));
      if (Array.isArray(res?.warnings) && res.warnings.length > 0) {
        console.info('[RunningHubJobRecoveryBar] active list warnings', res.warnings);
      }
    } catch (e) {
      const isGlobal =
        isApiError(e) &&
        e.status === 503 &&
        (e.serverCode === 'RH_GLOBAL_UNAVAILABLE' || e.serverCode === 'JOB_SERVICE_UNAVAILABLE');
      if (isGlobal) {
        setGlobalMaintenance(true);
        setProcessing([]);
        setCompletedUnacked([]);
        setListDegraded(false);
      }
      console.warn('[RunningHubJobRecoveryBar] active fetch failed', e);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setProcessing([]);
      setCompletedUnacked([]);
      setGlobalMaintenance(false);
      setListDegraded(false);
      return;
    }
    void refreshActive();
    const id = setInterval(() => {
      void refreshActive();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated, refreshActive]);

  useEffect(() => {
    if (!isAuthenticated || !processing.length) return;
    let cancelled = false;
    const pollProcessing = async () => {
      for (const job of processing) {
        if (cancelled) return;
        try {
          await api.getRunningHubJob(job.id);
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) void refreshActive();
    };
    const id = setInterval(() => {
      void pollProcessing();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isAuthenticated, processing, refreshActive]);

  /** 已完成且历史已确认写入时自动 ack，避免「已完成，点击查看」长期悬挂 */
  useEffect(() => {
    if (!isAuthenticated || !completedUnacked.length) return;

    let cancelled = false;

    const tryAutoAckCompleted = async () => {
      let anyAcked = false;

      for (const job of completedUnacked) {
        if (cancelled) return;
        if (appliedAckRef.current.has(job.id) || autoAckInFlightRef.current.has(job.id)) continue;
        if (loadingId === job.id) continue;

        const decision = evaluateRunningHubJobAutoAck(job, userEmail);
        if (!decision.shouldAck || !decision.reason) continue;

        autoAckInFlightRef.current.add(job.id);
        try {
          console.info('[RunningHubJobRecoveryBar] auto-ack', {
            jobId: job.id,
            feature_type: job.feature_type,
            history_client_item_id: job.history_client_item_id,
            reason: decision.reason,
            detail: decision.detail ?? null,
          });
          await api.ackRunningHubJob(job.id);
          appliedAckRef.current.add(job.id);
          anyAcked = true;
        } catch (e) {
          console.warn('[RunningHubJobRecoveryBar] auto-ack failed', {
            jobId: job.id,
            feature_type: job.feature_type,
            error: e instanceof Error ? e.message : String(e),
          });
        } finally {
          autoAckInFlightRef.current.delete(job.id);
        }
      }

      if (!cancelled && anyAcked) {
        await refreshActive();
      }
    };

    void tryAutoAckCompleted();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, completedUnacked, loadingId, refreshActive]);

  const handleViewCompleted = async (job: RunningHubActiveJobSummary) => {
    if (loadingId || appliedAckRef.current.has(job.id)) return;
    setLoadingId(job.id);
    try {
      const detail = await api.getRunningHubJob(job.id);
      const payload = (detail as { history_payload?: Record<string, unknown> | null })?.history_payload;
      if (!payload || typeof payload !== 'object') {
        throw new Error(zh ? '无法加载结果' : 'Cannot load result');
      }
      openPanelForRunningHubFeature(job.feature_type, panelOpeners);
      onRecoverCompleted({
        jobId: job.id,
        featureType: job.feature_type,
        historyPayload: payload,
      });
      await api.ackRunningHubJob(job.id);
      appliedAckRef.current.add(job.id);
      await refreshActive();
    } catch (e) {
      console.error('[RunningHubJobRecoveryBar] recover failed', e);
    } finally {
      setLoadingId(null);
    }
  };

  if (!isAuthenticated) return null;

  if (globalMaintenance) {
    return (
      <div
        className="fixed bottom-4 left-1/2 z-[12000] flex max-w-[min(520px,calc(100vw-24px))] -translate-x-1/2 flex-col gap-2"
        role="status"
        aria-live="polite"
      >
        <div className="rounded-lg border border-red-500/40 bg-zinc-900/95 px-4 py-2.5 text-sm text-red-100 shadow-lg backdrop-blur">
          {zh
            ? '任务恢复服务暂时不可用，请稍后重试'
            : 'Job recovery is temporarily unavailable. Please try again later.'}
        </div>
      </div>
    );
  }

  if (!processing.length && !completedUnacked.length && !listDegraded) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 z-[12000] flex max-w-[min(520px,calc(100vw-24px))] -translate-x-1/2 flex-col gap-2"
      role="status"
      aria-live="polite"
    >
      {listDegraded && !processing.length && !completedUnacked.length ? (
        <div className="rounded-lg border border-zinc-500/40 bg-zinc-900/95 px-4 py-2.5 text-sm text-zinc-300 shadow-lg backdrop-blur">
          {zh ? '部分任务记录暂不可用' : 'Some task records are temporarily unavailable'}
        </div>
      ) : null}
      {processing.map((job) => (
        <div
          key={job.id}
          className="rounded-lg border border-amber-500/40 bg-zinc-900/95 px-4 py-2.5 text-sm text-amber-100 shadow-lg backdrop-blur"
        >
          {zh
            ? `${labelFor(job.feature_type, language)}：生成中…`
            : `${labelFor(job.feature_type, language)}: Processing…`}
        </div>
      ))}
      {completedUnacked.map((job) => (
        <button
          key={job.id}
          type="button"
          disabled={loadingId === job.id}
          onClick={() => void handleViewCompleted(job)}
          className="w-full rounded-lg border border-emerald-500/50 bg-zinc-900/95 px-4 py-2.5 text-left text-sm text-emerald-100 shadow-lg backdrop-blur transition hover:bg-zinc-800 disabled:opacity-60"
        >
          {zh
            ? `${labelFor(job.feature_type, language)}：已完成，点击查看`
            : `${labelFor(job.feature_type, language)}: Done — tap to view`}
        </button>
      ))}
    </div>
  );
}
